import React from "react";
import { Link } from "react-router-dom";
import ServiceLandingPage, {
  InternalLink,
} from "../components/ServiceLandingPage";
import { COMPANY } from "../content";
import BrandLogosSection from "../components/BrandLogosSection";
import FeatureImageGrid from "../components/FeatureImageGrid";
import AISOBlock from "../components/AISOBlock";
import MotionMedia from "../components/MotionMedia";
import CinematicServiceHero from "../components/CinematicServiceHero";
import { videoJsonLd } from "../lib/videoSchema";

// Compact media-backed design stories driven by the final Higgsfield clips
// (kitchen-renovation, pantry-storage). "German-inspired" / "Italian-inspired"
// describe design influence only — not country of manufacture or any supplier
// relationship. Metallic finishes are showcased in the palette concepts below.
function KitchenMotionStories() {
  const stories = [
    {
      key: "kitchen-renovation",
      kicker: "Design Story · Renovation",
      title: "From strip-out to a kitchen that feels inevitable.",
      body: "German-inspired functional planning — work triangles, full-extension drawer banks and considered landing zones — paired with Italian-inspired material warmth in veneer, lacquer and stone. A design influence we apply to your space; every kitchen is built at our own Sharjah atelier.",
    },
    {
      key: "pantry-storage",
      kicker: "Design Story · Intelligent Storage",
      title: "Pull-out pantries, inner drawers and space-saving fittings.",
      body: "Tall pull-out pantry columns, inner drawers behind clean fronts, corner carousels and slim bottle pull-outs — specified to your cabinet dimensions and confirmed with the final hardware specification at survey, so every centimetre works.",
    },
  ];
  return (
    <section data-testid="kitchen-motion-stories" className="bg-[#1C110C]">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-20 md:py-28 grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10">
        {stories.map((s) => (
          <div key={s.key} data-testid={`kitchen-story-${s.key}`}>
            <MotionMedia
              mediaKey={s.key}
              alt={s.title}
              mode="card"
              aspectClass="aspect-[16/10]"
              testid={`kitchen-media-${s.key}`}
            />
            <p className="mt-6 text-[10px] tracking-[0.32em] uppercase text-[#D4A963]">{s.kicker}</p>
            <h3 className="mt-3 font-serif-display text-2xl md:text-3xl text-[#FAF5EC] leading-snug">{s.title}</h3>
            <p className="mt-3 text-sm md:text-base text-[#EDE1C8]/75 leading-[1.85]">{s.body}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

// Kitchen visuals — sourced from existing project & site images. Easy to
// replace later: just edit the `image` paths below.
const HERO_IMAGE = "/projects/address-hotel-2bhk-luxury-kitchen-fluted-glass.webp";

const SCOPE = [
  {
    name: "Demolition & site preparation",
    desc: "Full kitchen strip-out — old cabinetry, tiling, plumbing and electrical points removed cleanly. Dust barriers, floor protection and waste disposal handled in-house.",
  },
  {
    name: "Plumbing, electrical & gas reroutes",
    desc: "Sink, dishwasher, oven, hob and pantry-light circuits re-planned and certified. DEWA / SEWA-compliant work coordinated with your appliance schedule.",
  },
  {
    name: "Bespoke cabinetry & island joinery",
    desc: "Tall units, base units, drawer banks and statement islands built at our Sharjah atelier in lacquer, veneer, fluted oak or matt PET — to your exact dimensions.",
  },
  {
    name: "Stone countertops & splashbacks",
    desc: "Calacatta, Statuario, Dekton, quartz and porcelain slabs — templated, fabricated and installed with mitred edges, undermount sinks and integrated drainage grooves.",
  },
  {
    name: "Appliance integration",
    desc: "Bosch, Siemens, Teka, Gaggenau, Miele or Fhiaba ovens, hobs, hoods, fridges and wine cabinets fully integrated, levelled, ventilated and tested.",
  },
  {
    name: "Lighting, finishes & handover",
    desc: "Under-cabinet LEDs, pendants, dimming circuits, snag list, deep clean and walkthrough — kitchen handed over fully functional and styled.",
  },
];

// "From Concept to Completion" — process steps now with images
const PROCESS = [
  {
    n: "01",
    title: "On-site survey & brief",
    body: "We visit your villa or apartment, measure the existing kitchen, review plumbing and ventilation positions, and listen to how you actually cook and entertain.",
    image: "/media/team-projects/time-furniture-site-planning-project-coordination.webp",
    imageAlt: "On-site discussion and project coordination during a Time Furniture fit-out",
  },
  {
    n: "02",
    title: "Layout, 3D & itemised BOQ",
    body: "Concept layouts, 3D renders, finish samples, appliance schedule and an itemised BOQ — so you can compare cabinetry, stone and appliance costs separately, with no hidden lump sums.",
    image: "/projects/custom-joinery-macassar-veneer-high-gloss-detail.webp",
    imageAlt: "High-gloss macassar veneer cabinetry sample for custom kitchen design Dubai",
  },
  {
    n: "03",
    title: "Manufacturing at our atelier",
    body: "Carcasses, doors, drawers and stone fabrication run in parallel at our Sharjah workshop. Quality-checked before they leave — not after they arrive on site.",
    image: "/media/owner-projects/time-furniture-installation-team-joinery-time-furniture.webp",
    imageAlt: "Time Furniture installation team assembling joinery",
  },
  {
    n: "04",
    title: "On-site installation",
    body: "Demolition → MEP first-fix → cabinetry installation → stone templating & fitting → appliances → second-fix lighting → snag. Single project lead, no subcontractor chain.",
    image: "/media/team-projects/time-furniture-electrical-controls-panel-installation.webp",
    imageAlt: "Time Furniture technician working on an electrical controls panel",
  },
  {
    n: "05",
    title: "Snag, clean & handover",
    body: "Final adjustment of doors, drawers and soft-close, deep clean, appliance demo, and a defects-warranty period covering joinery, stone and installation.",
    image: "/media/owner-projects/kitchen-white-island-breakfast-seating-time-furniture.webp",
    imageAlt: "Completed white kitchen island with breakfast seating handed over by Time Furniture",
  },
];

// "Kitchen Details We Handle" feature grid
const KITCHEN_DETAILS = [
  {
    kicker: "Cabinetry",
    title: "Custom cabinets & joinery",
    desc: "Lacquer, veneer, fluted oak or matt PET — built at our Sharjah atelier with Blum and Hettich soft-close hardware as standard.",
    image: "/media/owner-projects/kitchen-full-height-white-cabinets-open-plan-time-furniture.webp",
    alt: "Full-height white kitchen cabinets with integrated storage and a contrasting stone island by Time Furniture",
  },
  {
    kicker: "Countertops",
    title: "Stone & marble surfaces",
    desc: "Calacatta, Statuario, Dekton, quartz and porcelain slab — templated and fabricated in-house with mitred edges and undermount sinks.",
    image: "/media/owner-projects/kitchen-timber-cabinetry-stone-worktop-time-furniture.webp",
    alt: "Timber kitchen wall units above pale base cabinets and a stone worktop by Time Furniture",
  },
  {
    kicker: "Appliances",
    title: "Integrated appliances",
    desc: "Bosch, Siemens, Teka, Gaggenau, Miele or Fhiaba — ovens, hobs, hoods, fridges and wine cabinets fully integrated, ventilated and tested.",
    image: "/media/owner-projects/kitchen-integrated-ovens-timber-tall-cabinets-time-furniture.webp",
    alt: "Built-in ovens integrated into warm timber kitchen cabinetry by Time Furniture",
  },
  {
    kicker: "Lighting",
    title: "Lighting & smart controls",
    desc: "Under-cabinet LEDs, pendants over the island, dimming circuits and smart switch integration — designed alongside the cabinetry, not added after.",
    image: "/media/team-projects/time-furniture-electrical-controls-panel-installation.webp",
    alt: "Time Furniture technician working on an electrical controls panel",
  },
  {
    kicker: "Storage",
    title: "Pantry & internal storage",
    desc: "Tall pantries, drawer banks, pull-out trays, corner solutions and waste/recycling stations — sized to your actual cookware and routine.",
    image: "/media/owner-projects/kitchen-pocket-door-appliance-pantry-time-furniture.webp",
    alt: "Pocket-door appliance pantry with internal storage built by Time Furniture",
  },
];

const FAQS = [
  {
    q: "How long does a kitchen renovation take in Dubai?",
    a: "A typical kitchen renovation in Dubai takes 4–7 weeks of on-site work, plus 2–3 weeks for design, BOQ approval and joinery manufacturing at our atelier. Larger islands, imported stone and premium appliance integration (Gaggenau, Miele, Fhiaba) can extend the timeline by 1–2 weeks. Time Furniture provides a fixed handover date in every contract.",
  },
  {
    q: "How much does a kitchen renovation cost in Dubai?",
    a: "Kitchen renovation cost in Dubai typically ranges from AED 45,000 for a refresh (doors + countertop only) to AED 350,000+ for a full bespoke kitchen with imported stone, fluted joinery and premium-tier appliances. We price cabinetry, stone, appliances and MEP separately so you can see exactly where the budget sits.",
  },
  {
    q: "What materials and finishes can I choose from?",
    a: "We offer matt and high-gloss lacquer, real wood veneer (oak, walnut, ash), fluted and ribbed panels, matt PET in solid and stone-effect colours, and full-aluminium frames. Countertops include Calacatta and Statuario marble, Dekton, quartz, porcelain slabs and solid surface — all fabricated in-house.",
  },
  {
    q: "Can I keep my existing appliances or do I have to upgrade?",
    a: "We can absolutely re-integrate your existing appliances if their dimensions and ventilation requirements are compatible with the new layout. We'll review the appliance list during the survey and flag any that won't fit, ventilate or perform well in the new design — so you decide what stays and what's upgraded.",
  },
  {
    q: "Do you provide a warranty on the kitchen?",
    a: "Yes. Time Furniture provides a written defects warranty covering joinery, hinges, drawer channels, stone installation and electrical fittings. Hardware (Blum, Hettich, Häfele) and appliances (Bosch, Siemens, Gaggenau, Miele, Fhiaba etc.) carry their own manufacturer warranties on top.",
  },
];

const WHY = [
  {
    t: "In-house atelier",
    d: "Cabinetry and stone are built at our own Sharjah workshop — full control over quality, lead times and finish consistency.",
  },
  {
    t: "Premium hardware as standard",
    d: "Blum, Hettich, Häfele, Grass — soft-close, smooth-running fittings designed for long-term daily use, not just opening day.",
  },
  {
    t: "Appliance-tier flexibility",
    d: "Bosch / Siemens / Teka for performance, Gaggenau / Miele / Fhiaba for premium — we integrate to your spec, not ours.",
  },
];

const BRAND_CATEGORIES = [
  {
    label: "Hardware & Accessories",
    groups: [
      {
        brands: [
          { name: "Blum", country: "Austria" },
          { name: "Hettich", country: "Germany" },
          { name: "Häfele", country: "Germany" },
          { name: "Grass", country: "Austria" },
          { name: "Salice", country: "Italy" },
        ],
      },
    ],
  },
  {
    label: "Kitchen Appliances",
    groups: [
      {
        tier: "Standard",
        brands: [{ name: "Bosch" }, { name: "Siemens" }, { name: "Teka" }],
      },
      {
        tier: "Premium",
        brands: [{ name: "Gaggenau" }, { name: "Miele" }, { name: "Fhiaba" }],
      },
    ],
  },
];

// Signature palette concepts — three metallic palette motion cards + a surfaces
// sampler. Clips are concept visualisations (final asset bundle pending), not
// actual project or brand-certified footage. Palette names are illustrative.
const PALETTES = [
  {
    key: "kitchen-bronze",
    name: "Brushed Bronze",
    setting: "Dubai villa concept",
    pairing: "Brushed bronze fronts with smoked oak and a warm quartzite worktop (natural stone).",
  },
  {
    key: "kitchen-silver",
    name: "Silver",
    setting: "Dubai apartment concept",
    pairing: "Silver metallic fronts with olive-tone cabinetry and a black granite worktop (natural stone).",
  },
  {
    key: "kitchen-champagne",
    name: "Champagne",
    setting: "Palm villa concept",
    pairing: "Champagne metallic fronts with taupe joinery and a Calacatta natural marble worktop.",
  },
];

function KitchenPaletteConcepts() {
  return (
    <section data-testid="kitchen-palette-concepts" className="bg-[#231410]">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-20 md:py-28">
        <p className="text-[10px] tracking-[0.32em] uppercase text-[#D4A963]">Signature Palette Concepts</p>
        <h2 className="mt-4 font-serif-display text-3xl md:text-5xl tracking-tight leading-[1.05] text-[#FAF5EC] max-w-3xl">
          Three metallic palettes, <em className="text-[#D4A963] not-italic">visualised</em>.
        </h2>
        <p className="mt-5 max-w-2xl text-sm md:text-base text-[#EDE1C8]/70 leading-[1.85]" data-testid="kitchen-palette-disclaimer">
          Design directions to explore — palette names are illustrative, and final
          finishes are always approved from physical slab and finish samples.
        </p>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-10">
          {PALETTES.map((p) => (
            <div key={p.key} data-testid={`kitchen-palette-${p.key}`}>
              <MotionMedia
                mediaKey={p.key}
                alt={`${p.name} kitchen palette concept — ${p.pairing}`}
                mode="card"
                aspectClass="aspect-[16/10]"
                testid={`kitchen-media-${p.key}`}
              />
              <div className="mt-5 flex items-baseline justify-between gap-3">
                <h3 className="font-serif-display text-2xl text-[#FAF5EC]">{p.name}</h3>
                <span className="text-[10px] tracking-[0.24em] uppercase text-[#D4A963]/80">{p.setting}</span>
              </div>
              <p className="mt-2 text-sm text-[#EDE1C8]/75 leading-[1.8]">{p.pairing}</p>
            </div>
          ))}
        </div>
        <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10 items-center" data-testid="kitchen-palette-surfaces">
          <MotionMedia
            mediaKey="kitchen-surfaces"
            alt="Five kitchen worktop material samples — granite, natural marble, quartz, quartzite and marble-look porcelain"
            mode="card"
            aspectClass="aspect-[16/10]"
            testid="kitchen-media-kitchen-surfaces"
          />
          <div>
            <p className="text-[10px] tracking-[0.32em] uppercase text-[#D4A963]">Material Sampler</p>
            <h3 className="mt-3 font-serif-display text-2xl md:text-3xl text-[#FAF5EC] leading-snug">
              Five surfaces, side by side.
            </h3>
            <p className="mt-3 text-sm md:text-base text-[#EDE1C8]/75 leading-[1.85]">
              Granite, natural marble, engineered quartz, natural quartzite and marble-look
              porcelain — compared honestly below. Natural marble and quartzite are quarried
              stone with one-of-a-kind veining; quartz is engineered for consistency; marble-look
              porcelain gives the veining aesthetic in a ceramic slab, not natural stone.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

// Worktop surfaces — practical tradeoffs, no prices, samples by consultation.
const WORKTOPS = [
  { name: "Granite", tag: "Natural stone", pros: "Very hard, heat-tolerant natural stone with unique grain.", care: "Periodic sealing recommended; acids can dull polish over time." },
  { name: "Marble", tag: "Natural stone", pros: "Unmatched natural veining — Calacatta and Statuario are our classics. Not to be confused with marble-look porcelain.", care: "Softer and porous; seal regularly and wipe acids (lemon, vinegar) quickly — it patinas with use." },
  { name: "Quartz", tag: "Engineered", pros: "Engineered stone — consistent pattern, non-porous, low-maintenance. Distinct from natural quartzite.", care: "Avoid direct hot pans and prolonged UV; use trivets." },
  { name: "Quartzite", tag: "Natural stone", pros: "Natural quarried stone harder than marble with marble-like veining — not the same as engineered quartz.", care: "Needs sealing; confirm the slab is true quartzite at selection." },
  { name: "Porcelain / Sintered", tag: "Ceramic slab", pros: "Thin, extremely heat- and scratch-resistant; marble-look porcelain offers the veining aesthetic without being natural marble. Dekton is our named sintered option.", care: "Edges and chips are harder to repair invisibly; mitred edges recommended." },
];

function KitchenWorktopsSection() {
  return (
    <section data-testid="kitchen-worktops" className="bg-[#FAF5EC] border-y border-[#6E1F2C]/15">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-16 md:py-24">
        <p className="eyebrow mb-5">Worktop Surfaces</p>
        <h2 className="font-serif-display text-3xl md:text-5xl tracking-tight leading-[1.05] text-[#1C110C] max-w-3xl">
          Five surfaces, honestly compared.
        </h2>
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {WORKTOPS.map((w) => (
            <div key={w.name} data-testid={`worktop-${w.name.split(" ")[0].toLowerCase()}`} className="bg-white border border-[#6E1F2C]/15 p-5">
              <p className="font-serif-display text-xl text-[#1C110C]">{w.name}</p>
              <p className="mt-1 text-[9px] tracking-[0.24em] uppercase text-[#6E1F2C]">{w.tag}</p>
              <p className="mt-2 text-xs text-[#3E2A22] leading-relaxed">{w.pros}</p>
              <p className="mt-2 text-xs text-[#7A6B5F] leading-relaxed italic">{w.care}</p>
            </div>
          ))}
        </div>
        <p className="mt-6 text-xs text-[#7A6B5F]" data-testid="worktop-samples-note">
          Physical slab and finish samples are shown at your design consultation — every surface is templated and fabricated in-house. Final selection is always approved from physical slabs, never from screen visuals.
        </p>

        {/* Premium appliance options — accurate categories, no dealer claims */}
        <div className="mt-12 border-t border-[#6E1F2C]/15 pt-10" data-testid="kitchen-appliance-options">
          <p className="eyebrow mb-4">Premium Appliance Options</p>
          <h3 className="font-serif-display text-2xl md:text-3xl text-[#1C110C] max-w-2xl">
            Gaggenau &amp; Miele, alongside Bosch, Siemens, Teka and Fhiaba.
          </h3>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl">
            <div className="bg-white border border-[#6E1F2C]/15 p-8" data-testid="appliance-option-gaggenau">
              <p className="text-[10px] tracking-[0.32em] uppercase text-[#D4A963]">Specification Option</p>
              <p className="mt-3 font-serif-display text-2xl text-[#1C110C]">Gaggenau</p>
              <p className="mt-3 text-sm text-[#3E2A22] leading-relaxed">
                Integrated ovens and steam cooking, refrigeration and wine climate
                cabinets, hobs and extraction — specified where the design and
                budget call for the very top tier.
              </p>
            </div>
            <div className="bg-white border border-[#6E1F2C]/15 p-8" data-testid="appliance-option-miele">
              <p className="text-[10px] tracking-[0.32em] uppercase text-[#D4A963]">Specification Option</p>
              <p className="mt-3 font-serif-display text-2xl text-[#1C110C]">Miele</p>
              <p className="mt-3 text-sm text-[#3E2A22] leading-relaxed">
                Ovens and steam cooking, hobs and extraction, dishwashers and
                built-in coffee machines — integrated, ventilated and tested as
                part of the cabinetry design.
              </p>
            </div>
          </div>
          <figure className="mt-6 max-w-4xl" data-testid="appliance-planning-photo">
            <img
              src="/media/owner-projects/kitchen-island-hob-integrated-extraction-time-furniture.webp"
              loading="lazy"
              decoding="async"
              alt="Kitchen island hob with integrated extraction installed by Time Furniture"
              className="w-full aspect-[21/9] object-cover object-center bg-[#EDE1C8]"
            />
            <figcaption className="mt-2 text-[10px] tracking-[0.14em] uppercase text-[#3E2A22]">Integrated appliance planning</figcaption>
          </figure>
          <p className="mt-5 text-xs text-[#7A6B5F] max-w-3xl" data-testid="appliance-options-note">
            Appliance brand, models and availability are confirmed per project during specification — your appliance schedule is itemised in the BOQ so you can compare options line by line.
          </p>
        </div>
      </div>
    </section>
  );
}

export default function KitchenRenovationDubai() {
  return (
    <ServiceLandingPage
      testid="kitchen-renovation-dubai-page"
      whatsappText={COMPANY.whatsappKitchenText}
      pageTitle="Kitchen Renovation Dubai | Bespoke Kitchen Contractor — Time Furniture"
      metaDescription="Premium kitchen renovation in Dubai — bespoke cabinetry, marble countertops, integrated appliances (Bosch, Siemens, Gaggenau, Miele, Fhiaba) and Blum/Hettich/Häfele hardware. Custom kitchens UAE, turnkey kitchen contractor since 1993."
      canonical="https://timefurniture.ae/kitchen-renovation-dubai"
      ogImage={HERO_IMAGE}
      serviceSchemaName="Kitchen Renovation — Dubai & UAE"
      breadcrumbLabel="Kitchen Renovation Dubai"
      extraJsonLd={[
        videoJsonLd({
          key: "kitchen-hero",
          name: "Bespoke kitchen renovation in Dubai — design visualization",
          description:
            "Burgundy kitchen design visualization by Time Furniture — bespoke cabinetry and a black marble island. Kitchen renovation concept, Dubai.",
          seconds: 10,
        }),
      ]}
      eyebrow="Kitchen Renovation · Dubai & UAE"
      h1="Bespoke Kitchen Renovation in"
      h1Accent="Dubai"
      heroImage={HERO_IMAGE}
      heroImageAlt="Luxury kitchen renovation in Dubai by Time Furniture — custom cabinetry, marble countertops, integrated appliances"
      heroMedia={null}
      heroOverride={
        <CinematicServiceHero
          mediaKey="kitchen-hero"
          posterAlt="Luxury burgundy kitchen with bespoke cabinetry, black marble island and integrated appliances"
          eyebrow="Kitchen Renovation · Dubai & UAE"
          sectionNumber="02"
          testid="kitchen-cinematic-hero"
          h1={
            <h1
              data-testid="kitchen-renovation-dubai-page-h1"
              className="font-serif-display text-4xl md:text-6xl lg:text-[72px] leading-[1.04] tracking-tight text-[#FAF5EC] max-w-4xl"
            >
              Bespoke Kitchen Renovation in <em className="text-[#D4A963] not-italic">Dubai</em>.
            </h1>
          }
          support={
            <p
              data-testid="kitchen-renovation-dubai-page-hero-subtext"
              className="mt-7 max-w-2xl text-[#EDE1C8]/90 leading-[1.7] text-base md:text-lg font-light"
            >
              Premium turnkey execution since 1993 · UAE-wide service · One contract, one accountable team.
            </p>
          }
        >
          <div className="mt-9 flex flex-col sm:flex-row gap-4">
            <Link
              to="/contact"
              data-testid="kitchen-renovation-dubai-page-hero-cta-quote"
              className="inline-flex items-center justify-center gap-3 bg-[#D4A963] text-[#1C110C] px-9 py-4 text-[11px] tracking-[0.32em] uppercase font-medium hover:bg-[#c9a166] transition-colors"
            >
              Get Quote
            </Link>
            <a
              href={`https://wa.me/${COMPANY.whatsapp}?text=${COMPANY.whatsappKitchenText}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="kitchen-renovation-dubai-page-hero-cta-whatsapp"
              className="inline-flex items-center justify-center gap-3 border border-[#FAF5EC]/55 text-[#FAF5EC] px-9 py-4 text-[11px] tracking-[0.32em] uppercase hover:bg-[#FAF5EC] hover:text-[#1C110C] transition-colors"
            >
              WhatsApp Us
            </a>
          </div>
        </CinematicServiceHero>
      }
      intro="Time Furniture is a bespoke kitchen renovation contractor in Dubai delivering complete, single-contract execution — from demolition and MEP through to joinery, stone, integrated appliances and final handover. Since 1993, we have built and rebuilt custom kitchens across the UAE — Palm Jumeirah, Dubai Hills, Emirates Hills, Damac Hills, Mohammed Bin Rashid City and beyond. Cabinetry is manufactured at our own Sharjah atelier in lacquer, veneer, fluted oak or matt PET; countertops are templated and fabricated in Calacatta, Statuario, Dekton, quartz or porcelain slab; and every kitchen ships with premium hardware (Blum, Hettich, Häfele) and your choice of standard or premium appliance package — so the kitchen performs as beautifully on year ten as it does on opening day."
      scope={SCOPE}
      process={PROCESS}
      whyChoose={WHY}
      faqs={FAQS}
      ctaHeading="Ready to renovate your"
      ctaAccent="kitchen in Dubai"
      ctaSubtext="Book a free site visit. We'll measure the kitchen, propose a layout and reply with a transparent BOQ — cabinetry, stone and appliances priced separately."
      featureGrid={
        <>
          <FeatureImageGrid
            testid="kitchen-details"
            eyebrow="What We Build"
            heading="Kitchen details we handle."
            subtext="From cabinetry to integrated appliances — every element is specified, manufactured and installed under one accountable team."
            items={KITCHEN_DETAILS}
            columns={3}
            aspect="aspect-[4/5]"
            bgClass="bg-[#FAF5EC] border-y border-[#6E1F2C]/15"
          />
          <KitchenMotionStories />
          <KitchenPaletteConcepts />
          <KitchenWorktopsSection />
        </>
      }
      aisoBlock={
        <AISOBlock
          testid="kitchen-aiso"
          audience={[
            "Villa owners upgrading their kitchen in Dubai or the wider UAE",
            "Apartment renovation clients (Downtown, Marina, Palm, Hills)",
            "Holiday home and Airbnb investors furnishing turnkey kitchens",
            "Commercial clients fitting out staff or hospitality kitchens",
          ]}
          whatWeDo="We design, build and execute complete kitchen renovation projects in Dubai — including demolition, MEP first-fix, bespoke joinery, marble and quartz countertops, integrated premium appliances, lighting, and a final snag-and-handover phase. Cabinetry is manufactured at our own Sharjah atelier so quality, lead time and finish stay under one roof."
          scope={[
            "Demolition & civil work",
            "Plumbing, gas & electrical re-routes",
            "Bespoke cabinetry & island joinery",
            "Stone countertops & splashbacks",
            "Integrated premium appliances",
            "Lighting, smart controls & finishing",
          ]}
          relatedLinks={[
            { kicker: "Joinery", label: "Custom Joinery & Cabinetry", to: "/custom-joinery-dubai" },
            { kicker: "Wardrobes", label: "Custom Wardrobes Dubai", to: "/custom-wardrobes-dubai" },
            { kicker: "Renovation", label: "Villa Renovation Dubai", to: "/villa-renovation-dubai" },
            { kicker: "Turnkey", label: "Interior Fit-Out Dubai", to: "/interior-fit-out-dubai" },
          ]}
        />
      }
      brandSections={
        <BrandLogosSection
          testid="kitchen-brands"
          eyebrow="Brands We Work With"
          heading="Premium Hardware & Appliance Brands We Work With"
          subtext="We specify and install globally trusted hardware, fittings, and appliances based on each project's design, performance requirements, and client preferences."
          categories={BRAND_CATEGORIES}
          disclaimer="Brand selection may vary based on project scope, availability, client preference, and budget."
        />
      }
      internalLinksLine={
        <>
          Also explore our{" "}
          <InternalLink to="/custom-wardrobes-dubai">custom wardrobes in Dubai</InternalLink>
          ,{" "}
          <InternalLink to="/villa-renovation-dubai">villa renovation services</InternalLink>
          , our{" "}
          <InternalLink to="/services">full interior fit-out services</InternalLink>
          , or see{" "}
          <InternalLink to="/ongoing-projects/samara">kitchens in our ongoing projects</InternalLink>
          .
        </>
      }
    />
  );
}
